import { FC, useEffect, useMemo, useState } from "react";
import HeaderMenu from "./header-menu.component.tsx";
import HeaderBalance from "./header-balance.component.tsx";
import { HeaderType } from "../../types/components/header.type.ts";
import { Link } from "react-router";
import { AppDispatch } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import {
  networksSelector,
  setBlockchains,
  setNetwork,
} from "../../../store/networks.store.ts";
import { useQuery } from "@apollo/client/react";
import { NODE_INFO_QUERY } from "../../../api/graphql/queries/node-info.query.ts";
import {
  NetworkInfoResponseType,
  NetworkInfoType,
} from "../../types/networks/networks.type.ts";
import { toast } from "react-toastify";
import { NotificationType } from "../../enums/notification.enum.ts";
import NotificationToast from "../toasts/notification.toast.tsx";
import { userSelector } from "../../../store/user.store.ts";
import { TbAuth2Fa } from "react-icons/tb";
import {
  PiShieldCheckDuotone,
  PiShieldCheckFill,
  PiShieldWarningDuotone,
} from "react-icons/pi";
import { Position } from "../../enums/position.enum.ts";
import Tooltip, { Size } from "../tooltip/tooltip.component.tsx";
import { useCookies } from "react-cookie";
import { getTfaLockCookieValue } from "../../utils/tfa-lock.util.ts";
const Header: FC<HeaderType> = ({
  sidebarOpen,
  setSidebarOpen,
  profileLoading,
  walletLoading,
}) => {
  const dispatcher: AppDispatch = useDispatch();
  const { network } = useSelector(networksSelector);
  const [tfaLockTimer, setTfaLockTimer] = useState<number | null>(null);
  const [cookies] = useCookies();

  const {
    profile: { id, isTfaEnabled, isPTfaEnabled },
  } = useSelector(userSelector);

  const networkInfo: useQuery.Result<NetworkInfoResponseType> =
    useQuery<NetworkInfoResponseType>(NODE_INFO_QUERY);

  useEffect(() => {
    const timerCookie = getTfaLockCookieValue(cookies, id);

    if (!timerCookie) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTfaLockTimer(null);
      return;
    }

    const lockDate = new Date(timerCookie);
    if (Number.isNaN(lockDate.getTime())) {
      setTfaLockTimer(null);
      return;
    }

    const updateTimer = () => {
      const remain = Math.max(
        0,
        Math.ceil((lockDate.getTime() - Date.now()) / 1000),
      );
      setTfaLockTimer(remain);

      if (remain <= 0) {
        setTfaLockTimer(0);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [cookies, id]);

  useEffect((): void => {
    if (networkInfo.data) {
      const response: NetworkInfoType = networkInfo.data.getNodeInfo;
      dispatcher(setNetwork(response.network));
      dispatcher(setBlockchains(response.blockchains));
    }
  }, [networkInfo.data, dispatcher]);

  useEffect((): void => {
    if (networkInfo.error) {
      toast(
        <NotificationToast
          type={NotificationType.ERROR}
          title="Networks Info"
          message="Failed to fetch networks info"
        />,
        {
          toastId: "networks-info-fetch-error",
        },
      );
    }
  }, [networkInfo.error]);

  const tfacmp = useMemo(() => {
    if (isTfaEnabled) {
      return <PiShieldCheckFill className="text-green-500" size={16} />;
    }

    if (isPTfaEnabled) {
      return <PiShieldCheckDuotone className="text-orange-300" size={16} />;
    }

    return (
      <PiShieldWarningDuotone
        className="text-red-500 animate-bounce"
        size={16}
      />
    );
  }, [isTfaEnabled, isPTfaEnabled]);

  const tfatooltip = useMemo(() => {
    if (isTfaEnabled) {
      return "Enabled";
    }

    if (isPTfaEnabled) {
      return "For approvals only";
    }

    return "Not enabled";
  }, [isTfaEnabled, isPTfaEnabled]);

  const tfaLockTimerText = useMemo((): string | null => {
    if (tfaLockTimer === null || tfaLockTimer <= 0) {
      return null;
    }

    const minutes = String(Math.floor(tfaLockTimer / 60)).padStart(2, "0");
    const seconds = String(tfaLockTimer % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  }, [tfaLockTimer]);

  return (
    <header className="sticky top-0 bg-white border-b border-slate-200 z-30">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 -mb-px">
          {/* Header: Left side */}
          <div className="flex">
            {/* Hamburger button */}
            <button
              className="text-slate-500 hover:text-slate-600 lg:hidden"
              aria-controls="sidebar"
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <span className="sr-only">Open sidebar</span>
              <svg
                className="w-6 h-6 fill-current"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="4" y="5" width="16" height="2" />
                <rect x="4" y="11" width="16" height="2" />
                <rect x="4" y="17" width="16" height="2" />
              </svg>
            </button>
            <div className="flex items-center m-1.5 space-x-2">
              {network && (
                <Link
                  to="/networks"
                  className={`relative text-xs inline-flex items-center justify-between font-medium ${network.toString().toLowerCase() === "testnet" ? "bg-rose-100 text-rose-600" : "bg-green-100 text-green-600"} rounded-full text-center px-2.5 py-1`}
                >
                  <span className="mr-1">
                    {network.toString().toLowerCase()}
                  </span>
                  <span className="relative flex h-2 w-2">
                    <span
                      className={`animate-ping absolute inline-flex h-full w-full rounded-full ${network.toString().toLowerCase() === "testnet" ? "bg-rose-400" : "bg-green-400"} opacity-75`}
                    ></span>
                    <span
                      className={`relative inline-flex rounded-full h-2 w-2 ${network.toString().toLowerCase() === "testnet" ? "bg-rose-500" : "bg-green-500"}`}
                    ></span>
                  </span>
                </Link>
              )}
              <span>&bull;</span>
              <Tooltip
                title="Two-factor authentication"
                message={tfatooltip}
                position={Position.BOTTOM}
              >
                <Link
                  to="/profile/security"
                  className="flex items-center relative space-x-0.5"
                >
                  {tfacmp}
                  <TbAuth2Fa size={24} />
                </Link>
              </Tooltip>
              {tfaLockTimerText && (
                <Tooltip
                  title="Two-factor authentication"
                  message="Temporary unlocked for approvals"
                  position={Position.BOTTOM}
                  size={Size.LG}
                >
                  <span className="text-xs text-orange-400">
                    {tfaLockTimerText}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          {/* Header: Right side */}
          <div className="flex items-center">
            {/*  Divider */}
            <HeaderBalance walletLoading={walletLoading} />
            <HeaderMenu align="right" profileLoading={profileLoading} />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
